// src/pages/admin/AdminAds.js
// Admin : publicités sponsorisées (MySQL)
// - Utilise l'identifiant SQL `id` (avec repli `_id`) pour éditer/supprimer.
// - N'envoie JAMAIS de valeur nulle au backend (évite le stockage du texte "null"/"NULL").
// - Aligne les champs sur le modèle SQL : position (placement) + active (boolean).
// - Aperçu image au bon ratio + dimensions recommandées (v1.167).
// - Labels au-dessus de chaque champ de la modale (v1.168).
// Maxula

import React, { useEffect, useState, useRef } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiUpload, FiEye, FiMousePointer } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { adAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';

// home_tiles : les 4 bannières cliquables du bloc « Comment ça marche » de l'accueil.
// Le champ « Annonceur » sert de sous-titre, « Libellé bouton » de texte du bouton.
// Sans aucune pub active sur cet emplacement, le bloc retombe automatiquement
// sur les 4 catégories les mieux fournies.
const PLACEMENTS = { home_top: 'Accueil (haut)', home_tiles: 'Accueil (4 bannières « Comment ça marche »)', home_mid: 'Accueil (milieu)', sidebar: 'Barre latérale', deals_top: 'Deals (haut)' };
const EMPTY = { title: '', link: '', advertiser: '', badge: '', ctaLabel: '', placement: 'home_top', status: 'active', sortOrder: 0 };

// Identifiant SQL (id) avec repli legacy Mongo (_id)
const adId = (a) => a?.id ?? a?._id;
// Nettoie une valeur avant envoi : neutralise null/undefined et le texte "null"/"NULL".
const sanitize = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined') return '';
  return s;
};

// Normalise un lien de pub : convertit une URL absolue vers localhost / IP de dev /
// le domaine courant en CHEMIN INTERNE relatif, pour ne jamais enregistrer
// "http://localhost:3000/deals/xxx" (qui casserait en production). Les vrais liens
// externes (autre domaine) sont conservés tels quels.
const normalizeLink = (v) => {
  const s = sanitize(v);
  if (!s) return '';
  if (s.startsWith('/') && !s.startsWith('//')) return s; // déjà interne
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : 'http://localhost';
    const u = new URL(s, origin);
    const host = (u.hostname || '').toLowerCase();
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');
    const isSameOrigin = (typeof window !== 'undefined' && window.location) && host === window.location.hostname.toLowerCase();
    if (isLocal || isSameOrigin) return (u.pathname + u.search + u.hash) || '/deals';
    return s;
  } catch (e) { return s; }
};

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef();

  const load = async () => { try { setLoading(true); const res = await adAPI.adminList(); setAds(res.data?.data || []); } catch (e) {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setImageFile(null); setPreview(null); setModal(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      ...EMPTY,
      ...a,
      placement: a.placement || a.position || 'home_top',
      status: (a.status || (a.active === false ? 'paused' : 'active')),
      title: sanitize(a.title), link: sanitize(a.link), advertiser: sanitize(a.advertiser),
      badge: sanitize(a.badge), ctaLabel: sanitize(a.ctaLabel)
    });
    setImageFile(null);
    setPreview(a.image ? normalizeImagePath(a.image) : null);
    setModal(true);
  };
  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const pickImg = (e) => { const f = e.target.files?.[0]; if (f) { setImageFile(f); setPreview(URL.createObjectURL(f)); } };

  const save = async () => {
    if (!sanitize(form.title)) return toast.error('Titre requis');
    try {
      setSaving(true);
      const fd = new FormData();
      ['title', 'link', 'advertiser', 'badge', 'ctaLabel'].forEach((k) => fd.append(k, k === 'link' ? normalizeLink(form[k]) : sanitize(form[k])));
      fd.append('placement', form.placement || 'home_top');
      fd.append('active', form.status === 'paused' ? 'false' : 'true');
      if (imageFile) fd.append('image', imageFile);
      const id = adId(editing);
      if (editing && id != null) await adAPI.update(id, fd); else await adAPI.create(fd);
      toast.success(editing ? 'Publicité mise à jour' : 'Publicité créée');
      setModal(false); load();
    } catch (e) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const remove = async (a) => { if (!window.confirm('Supprimer cette publicité ?')) return; try { await adAPI.remove(adId(a)); toast.success('Supprimée'); load(); } catch (e) { toast.error('Erreur'); } };

  // Le cadre d'aperçu doit refléter le VRAI cadre d'affichage, sinon l'admin
  // téléverse une image paysage pour une tuile portrait et le sujet est coupé.
  const imgSpec = form.placement === 'home_tiles'
    ? { label: '600 × 800 px', note: 'ratio 3:4, PORTRAIT — c\'est une tuile haute, pas une bannière.', ratio: '3 / 4', boxClass: 'w-28' }
    : { label: '880 × 340 px', note: 'ratio ~2.6:1, paysage. Min. 600 px de large.', ratio: '2.6 / 1', boxClass: 'w-44' };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-3xl font-bold text-gray-900">Publicités</h1><p className="text-gray-500">Bannières sponsorisées affichées sur le site.</p></div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"><FiPlus /> Nouvelle pub</button>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ads.map((a) => {
            const active = a.status ? a.status === 'active' : a.active !== false;
            const placementKey = a.placement || a.position;
            return (
            <div key={adId(a)} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {a.image ? <img src={normalizeImagePath(a.image)} alt={a.title} className="w-full h-32 object-cover" /> : <div className="h-32 bg-gradient-to-r from-primary-600 to-blue-700" />}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-900">{a.title}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{active ? 'active' : 'paused'}</span>
                </div>
                <div className="text-xs text-gray-400 mt-1">{PLACEMENTS[placementKey] || placementKey}</div>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1"><FiEye size={12} /> {a.impressions || 0}</span>
                  <span className="inline-flex items-center gap-1"><FiMousePointer size={12} /> {a.clicks || 0}</span>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(a)} className="p-2 text-blue-600 hover:bg-blue-50 rounded"><FiEdit size={16} /></button>
                  <button onClick={() => remove(a)} className="p-2 text-red-600 hover:bg-red-50 rounded"><FiTrash2 size={16} /></button>
                </div>
              </div>
            </div>
            );
          })}
          {ads.length === 0 && <div className="col-span-full text-center text-gray-400 py-12">Aucune publicité.</div>}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Modifier' : 'Nouvelle'} publicité</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Titre <span className="text-red-500">*</span></label>
                <input name="title" value={form.title} onChange={onField} placeholder="Ex : Reveal Your Natural Radiance" className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Annonceur</label>
                <input name="advertiser" value={form.advertiser} onChange={onField} placeholder="Ex : AbodyTeam" className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Lien</label>
                <input name="link" value={form.link} onChange={onField} placeholder="https://... ou /deals/mon-deal" className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Réduction / badge</label>
                  <input name="badge" value={form.badge} onChange={onField} placeholder="Ex : -40%" className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Texte du bouton</label>
                  <input name="ctaLabel" value={form.ctaLabel} onChange={onField} placeholder="Ex : Voir l'offre" className="w-full border rounded-lg px-3 py-2" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Emplacement</label>
                  <select name="placement" value={form.placement} onChange={onField} className="w-full border rounded-lg px-3 py-2">
                    {Object.entries(PLACEMENTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {form.placement === 'home_tiles' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Le champ <strong>Annonceur</strong> sert de description sous le titre.
                      Créez 4 publicités sur cet emplacement.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select name="status" value={form.status} onChange={onField} className="w-full border rounded-lg px-3 py-2">
                    <option value="active">Active</option><option value="paused">En pause</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image de la publicité</label>
                <p className="text-xs text-gray-500 mb-2">
                  Format recommandé : <strong>{imgSpec.label}</strong> ({imgSpec.note})
                  JPG/WebP/PNG, &lt; 300 Ko. L'image est recadrée (cover) pour remplir le
                  cadre — gardez le sujet bien centré.
                </p>
                <div className="flex items-center gap-3">
                  <div className={`rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center ${imgSpec.boxClass}`} style={{ aspectRatio: imgSpec.ratio }}>
                    {preview ? <img src={preview} alt="aperçu" className="w-full h-full object-cover" /> : <span className="text-xs text-gray-300 px-2 text-center">Aperçu (cadre réel)</span>}
                  </div>
                  <button onClick={() => imgRef.current?.click()} className="px-3 py-2 rounded-lg border text-sm flex items-center gap-1 hover:bg-gray-50"><FiUpload size={14} /> Image</button>
                  <input ref={imgRef} type="file" accept="image/*" onChange={pickImg} className="hidden" />
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(false)} className="px-4 py-2 rounded-lg border hover:bg-gray-50">Annuler</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60">{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAds;
